import { Box, Flex, Text } from "@chakra-ui/react";
import type { StyleSpecification } from "maplibre-gl";
import { MapRef, useMap } from "react-map-gl/maplibre";

import { GeometryType } from "@nens/rana/api-v1";

import { LegendRow } from "./components/legend-row";
import { Swatch } from "./features/swatch";
import { extractLegendEntries } from "./helpers/extract-legend-entries";

export interface MaplibreLegendLayer {
  /** The style layers to derive legend entries from. */
  styleLayers: StyleSpecification["layers"];
  /** Unique identifier for this layer, used as the legend item label fallback. */
  layer: string;
  /** GeoJSON geometry type of the layer, used to pick the group icon. */
  type: GeometryType | undefined;
}

export interface MaplibreLegendProps {
  /** Layers to show in the legend, already filtered to only the visible ones. */
  layers: MaplibreLegendLayer[];
  /** Map id from react-map-gl's MapProvider. Pass "current" to use the current map. */
  mapId: string;
  /**
   * For the publication legends, we will show 1 legend entry per Maplibre layer.
   * Meaning the group label is not important and actually clutters the legend.
   * This flag allows to ignore the grouping and show all entries as if they don't belong to any group.
   */
  ignoreGrouping?: boolean;
}

interface LegendGroupProps {
  label: string;
  type: GeometryType | undefined;
  styleLayers: StyleSpecification["layers"] | undefined;
  map: MapRef | undefined;
  ignoreGrouping?: boolean;
}

/** Renders one layer group. */
function LegendGroup({ label, type, styleLayers, map, ignoreGrouping }: LegendGroupProps) {
  const GAP_Y = 1;
  const GAP_X = 2;
  const entries = extractLegendEntries(styleLayers ?? []);

  if (entries.length === 0) return null;

  if (ignoreGrouping) {
    return (
      <Flex direction="column" gap={GAP_Y}>
        {entries.map((e) => (
          <LegendRow key={e.layerId} entry={e} map={map} />
        ))}
      </Flex>
    );
  }

  const isSimple = entries.length === 1;

  if (isSimple) {
    const entry = entries[0];
    return (
      <Flex fontWeight="semibold" sx={{ "*": { fontSize: "inherit" } }} align="center" gap={GAP_X}>
        <LegendRow entry={entry} map={map} />
        {/* Simple entry doesn't have a label so use the group label */}
        {!entry.layerLabel && (
          <Text fontWeight="semibold" fontSize="sm">
            {label}
          </Text>
        )}
      </Flex>
    );
  }

  return (
    <Box>
      <Flex mb={2} align="center" gap={GAP_X}>
        <Swatch>
          <Swatch.GroupIcon geometryType={type} />
        </Swatch>
        <Text fontWeight="semibold" fontSize="sm">
          {label}
        </Text>
      </Flex>
      <Flex pl={3} direction="column" gap={GAP_Y}>
        {entries.map((e) => (
          <LegendRow key={e.layerId} entry={e} map={map} />
        ))}
      </Flex>
    </Box>
  );
}

export function MaplibreLegend({ layers, mapId, ignoreGrouping }: MaplibreLegendProps) {
  const { [mapId]: map } = useMap();
  return (
    <Flex direction="column" gap={3}>
      {layers.map(({ styleLayers, layer, type }) => (
        <LegendGroup
          key={layer}
          label={layer}
          type={type}
          styleLayers={styleLayers}
          map={map}
          ignoreGrouping={ignoreGrouping}
        />
      ))}
    </Flex>
  );
}
